import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Typography, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useGetLaunchHistoryQuery } from '~/features/launch-history/model/api/launch-history-api';
import { DataTable } from '~/shared/ui/tables/DataTable';
import { defaultPaginationModel } from '~/shared/const/app';
import Dashboard from '~/features/dashboard/Dashboard';
import { IFilterConfig, IPaginationModel } from '~/shared/types/types';
import { usePersistedTableState } from '~/shared/hooks/usePersistedTableState';

import { formatCustomer } from '~/shared/utils/formatCustomer';

export const LaunchHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  
  const {
    activeFilters,
    setActiveFilters,
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
  } = usePersistedTableState('launch_history', defaultPaginationModel);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const sortBy = sortModel?.[0]?.field;
  const sortDir = sortModel?.[0]?.sort;

  const { data, isFetching, error, refetch } = useGetLaunchHistoryQuery({
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    search: paginationModel.search,
    filters: activeFilters || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
       field: 'account_name', 
      headerName: 'Аккаунт', 
      flex: 0,
      width: 300,
    },
    { 
      field: 'customer_id', 
      headerName: 'ID Клиента', 
      width: 150,
      renderCell: (params) => formatCustomer(params.value)
    },
    { 
      field: 'number_of_histories',  
      align: 'center',
      headerAlign: 'center',
      headerName: 'Кол-во запусков', 
      width: 180 
    },
    { 
      field: 'last_launch_date', 
      headerName: 'Дата последнего запуска', 
      width: 180,
    },
    { 
      field: 'last_launch_status', 
      headerName: 'Последний запуск', 
      width: 180,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip 
          label={params.value === 'success' ? 'Успешно' : params.value === 'error' || params.value === 'failed' ? 'Ошибка' : 'В ожидании'} 
          color={params.value === 'success' ? 'success' : params.value === 'error' || params.value === 'failed' ? 'error' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Детали',
      align: 'center',
      headerAlign: 'center',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          startIcon={<VisibilityIcon />}
          size="small"
          onClick={() => navigate(`/launch-history/account/${params.row.account_id}`)}
        >
        </Button>
      ),
    },
  ];

  const rows = data?.data || [];
  const rowCount = data?.pagination?.total || 0;

  const filterColumns: IFilterConfig[] = useMemo(
    () => [
      { key: 'account_name', label: 'Аккаунт', type: 'text' },
      { key: 'customer_id', label: 'ID Клиента', type: 'text' },
      { key: 'date_from', label: 'Дата с', type: 'date' },
      { key: 'date_to', label: 'Дата по', type: 'date' },
      {
        key: 'status',
        label: 'Последний запуск',
        type: 'select',
        options: [
          { label: 'Успешно', value: 'success' },
          { label: 'Ошибка', value: 'failed' },
          { label: 'В ожидании', value: 'pending' },
        ],
      },
    ],
    [],
  );

  const onFilterApply = (filterState: Record<string, any>) => {
    const filters = {
      account_name: filterState.account_name || undefined,
      customer_id: filterState.customer_id || undefined,
      min_launches: filterState.min_launches || undefined,
      max_launches: filterState.max_launches || undefined,
      date_from: filterState.date_from || undefined,
      date_to: filterState.date_to ? `${filterState.date_to} 23:59:59` : undefined,
      status: filterState.status || undefined,
    };

    setActiveFilters(filters);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const onFilterReset = () => {
    setActiveFilters(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleSortModelChange = useCallback((model: any) => {
    setSortModel(model);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const loadingText = useMemo(() => {
    if (paginationModel.search || activeFilters) {
      return 'идет поиск';
    }
    return 'загрузка данных';
  }, [paginationModel.search, activeFilters]);

  return (
    <Dashboard>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>История создания кампаний</Typography>
        <DataTable
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          isFetching={isFetching}
          error={error}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={handleSortModelChange}
          sortingMode="server"
          filterColumns={filterColumns}
          onFilterApply={onFilterApply}
          onFilterReset={onFilterReset}
          loadingText={loadingText}
          onRefresh={handleRefresh}
          activeFilters={activeFilters}
        />
      </Box>
    </Dashboard>
  );
};
